import { useEffect, useRef } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { LandingPage } from './routes/Landing.js';
import { AppPage } from './routes/App.js';
import { LearnPage } from './routes/Learn.js';
import { EditorPage } from './routes/Editor.js';
import { LoginPage } from './routes/Login.js';
import { RoomPage } from './routes/Room.js';
import { DashboardPage } from './routes/Dashboard.js';
import { AnalyticsPage } from './routes/Analytics.js';
import { AccountPage } from './routes/Account.js';
import { DocumentsPage } from './routes/Documents.js';
import { SmartlakePage } from './routes/Smartlake.js';
import { WorkspacePage } from './routes/Workspace.js';
import { PlansPage } from './routes/Plans.js';
import { NotFoundPage } from './routes/NotFound.js';
import { AuthProvider } from './state/auth.js';
import { I18nProvider } from './state/i18n.js';
import { LegalPage } from './routes/Legal';
import { DesignSystemPage } from './routes/DesignSystem.js';
import { setActiveSiteTracker, SiteTracker } from './lib/site-tracker.js';
import { parseReferralUrl } from './lib/referral.js';
import { getVisitorId, saveReferralToken } from './lib/visitor.js';
import { api } from './api/client.js';
import { InboxRoute } from './routes/Inbox.js';
import { isCustomTenantHost } from './lib/auth-host.js';

function SiteTracking(): null {
  const location = useLocation();
  const tracker = useRef<SiteTracker | null>(null);

  useEffect(() => {
    const instance = new SiteTracker();
    tracker.current = instance;
    setActiveSiteTracker(instance);
    instance.start();
    return () => {
      if (tracker.current === instance) {
        tracker.current = null;
        setActiveSiteTracker(null);
      }
      instance.stop();
    };
  }, []);

  useEffect(() => {
    tracker.current?.pageView(location.pathname);
  }, [location.pathname]);

  return null;
}

// powered-by 배지가 실은 서명된 ?ref/src 로 이 랜딩에 착지시킨다.
// 서명 검증 후 (1) 클릭 1건 기록 (2) 리퍼럴 토큰 발급·저장 → 가입 시 auth 가 귀속한다.
// 전 과정 best-effort — 실패해도 페이지에 영향 0. 마운트당 1회만.
function ReferralCapture(): null {
  useEffect(() => {
    let parsed: ReturnType<typeof parseReferralUrl> = null;
    try {
      parsed = parseReferralUrl(window.location.href);
    } catch {
      parsed = null;
    }
    if (!parsed || !parsed.valid) return;
    const proposalId = parsed.src.startsWith('proposal_')
      ? parsed.src.slice('proposal_'.length)
      : parsed.src;
    if (!proposalId || !parsed.ref) return;
    const visitorId = getVisitorId();
    void api.recordPoweredByClick(proposalId, parsed.ref, visitorId).catch(() => {});
    void api
      .issueReferralToken(parsed.ref, proposalId, visitorId)
      .then((r) => {
        if (r.token) saveReferralToken(r.token);
      })
      .catch(() => {});
  }, []);
  return null;
}

export function App(): JSX.Element {
  const location = useLocation();
  // Sweetspot inbox has its own verified Sophy session. No CloudBTL auth/referral/tracking.
  if (location.pathname === '/inbox' || location.pathname === '/inbox/') {
    return <InboxRoute />;
  }
  const customLakeHome =
    typeof window !== 'undefined' &&
    isCustomTenantHost(window.location.hostname) &&
    location.pathname === '/';
  return (
    <I18nProvider>
      <AuthProvider>
        <SiteTracking />
        <ReferralCapture />
        <Routes>
          <Route path="/" element={customLakeHome ? <SmartlakePage /> : <LandingPage />} />
          <Route path="/app" element={<AppPage />} />
          <Route path="/learn" element={<LearnPage />} />
          <Route path="/edit/:proposalId" element={<EditorPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/r/:linkId" element={<RoomPage />} />
          <Route path="/dashboard/:proposalId" element={<DashboardPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/documents" element={<DocumentsPage />} />
          <Route path="/lake" element={<SmartlakePage />} />
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/design-system" element={<DesignSystemPage />} />
          <Route path="/legal/:doc" element={<LegalPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
    </I18nProvider>
  );
}
